// Which laws a move showed at work, each with the glyphs of that moment, so
// the Codex can keep the moment. Read from the engine's events.

import type { LawId } from "../config/laws.ts"
import { heldCards, seen, type Card, type CardId, type Column, type FoldEvent, type GameEvent, type MoveEvent, type TableState } from "./engine.ts"
import { keyOf, scaleOf, type GlyphKey } from "./rings.ts"

export type Sighting = { readonly law: LawId; readonly example: readonly GlyphKey[] }

type Shown = readonly [CardId, GlyphKey]

const shownIn = (col: Column): readonly Shown[] => col.cards.map((c): Shown => [c.id, keyOf(seen(c, col.mirror))])
const shownHeld = (c: Card): Shown => [c.id, keyOf(c.rings)]

// Every card on a table, by id, as it shows.
const shownOn = (state: TableState): ReadonlyMap<CardId, GlyphKey> => new Map([...state.cols.flatMap(shownIn), ...heldCards(state.altars).map(shownHeld)])

const keyIn = (shown: ReadonlyMap<CardId, GlyphKey>) => (id: CardId): readonly GlyphKey[] => {
  const k = shown.get(id)
  return k === undefined ? [] : [k]
}

// The glyphs of the given cards as they show on a table.
export const keysOn = (state: TableState, ids: readonly CardId[]): readonly GlyphKey[] => ids.flatMap(keyIn(shownOn(state)))

const landing = (e: MoveEvent): readonly Sighting[] => {
  if (e.to.kind === "altar") return [{ law: "altar", example: keysOn(e.after, e.ids) }]
  const col = e.after.cols[e.to.i]
  const first = e.ids[0]
  if (col === undefined || first === undefined) return []
  const at = col.cards.findIndex((c) => c.id === first)
  const below = col.cards[at - 1]
  const card = col.cards[at]
  if (below === undefined || card === undefined) return [{ law: "empty", example: keysOn(e.after, e.ids) }]
  return [{ law: "rest", example: [keyOf(seen(below, col.mirror)), keyOf(seen(card, col.mirror))] }]
}

const moveSightings = (e: MoveEvent): readonly Sighting[] => [
  ...landing(e),
  ...(e.ids.length > 1 ? [{ law: "run" as const, example: keysOn(e.after, e.ids) }] : []),
  ...(e.flip ? [{ law: "mirror" as const, example: keysOn(e.after, e.ids) }] : []),
]

const foldKeys = (folded: readonly Card[], mirror: boolean): readonly GlyphKey[] => folded.map((c) => keyOf(seen(c, mirror)))

// The three cards as they showed, then the card they made as it shows now
// (a tide may have turned over since).
const foldSighting = (e: FoldEvent): Sighting => {
  const madeMirror = e.after.cols[e.col]?.mirror ?? e.mirror
  const example = [...foldKeys(e.folded, e.mirror), keyOf(seen(e.card, madeMirror))]
  return { law: scaleOf(e.card.rings) > 2 ? "fold-high" : "fold", example }
}

const eventSightings = (e: GameEvent): readonly Sighting[] => {
  switch (e.type) {
    case "move":
      return moveSightings(e)
    case "fold":
      return [foldSighting(e)]
    case "turn":
      return [{ law: "tide", example: [] }]
    case "win":
      return [{ law: "monad", example: [] }]
  }
}

export const sightingsOf = (events: readonly GameEvent[]): readonly Sighting[] => {
  const folds = events.filter((e) => e.type === "fold").length
  return [...events.flatMap(eventSightings), ...(folds > 1 ? [{ law: "cascade" as const, example: [] }] : [])]
}
